import { Map, Set } from 'immutable';

import { getAffectingPropertyName, getFirstValue } from '@/utils/taxonomy/ebay/properties';
import { getOversetItemSpecifics } from '@/utils/product/taxonomy';
import { getCategoryError } from '@/utils/validations/details';
import { getSize } from '@/utils/iterable/getSize';
import { reduce } from '@/utils/iterable/reduce';
import { get } from '@/utils/iterable/get';

import { TAXONOMY_PROPERTY, VALUE } from '@/constants/product';
import { EMPTY, VELA } from '@/constants';
import { ATTRIBUTE } from '@/constants/attributes';
import { STATUS } from '@/constants/listings';
import { FORM } from '@/constants/forms';
import { DATA } from '@/data';

export const errors = {
  addedOverLimit({ limit }) {
    return `Item should not exceed ${limit} specifics`;
  },
  charachtersOverLimit({ limit, name }) {
    return `${name} should not exceed ${limit} characters`;
  },
  invalid({ name }) {
    return `Choose ${name}`;
  },
  nameCharachtersOverLimit({ limit }) {
    return `Item specific name should not exceed ${limit} characters`;
  },
  nameExist({ name }) {
    return `An item specific "${name}" already exists`;
  },
  selectedOverLimit({ name }) {
    return `${name} selected over limit`;
  },
  required({ name }) {
    return `${name} is required`;
  },
  valueCharachtersOverLimit({ limit }) {
    return `Item specific value should not exceed ${limit} characters`;
  },
};

export function validateItemSpecifics({ marketplaceId, product, shopData }) {
  const status = product.get('status');
  const channel = product.get('channel');
  const category = product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_CATEGORY, VALUE]);
  const itemSpecifics = product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS], EMPTY.MAP);
  const shopValidations = shopData.getIn(['validations', 'itemSpecifics'], EMPTY.MAP);

  if (
    getCategoryError({ category, channel, marketplaceId }) ||
    get([channel, marketplaceId, 'CATEGORIES', category, 'features', 'itemSpecifics'])(DATA) === false
  ) {
    return EMPTY.MAP;
  }

  let error = EMPTY.MAP;

  if (
    shopValidations.has('MaxItemSpecificsPerItem') &&
    getSize(itemSpecifics.get(VALUE)) > shopValidations.get('MaxItemSpecificsPerItem')
  ) {
    error = error.set(ATTRIBUTE.ITEM_SPECIFICS,
      errors.addedOverLimit({ limit: shopValidations.get('MaxItemSpecificsPerItem') })
    );
  }

  const properties = get([channel, marketplaceId, 'CATEGORIES', category, 'properties'])(DATA);
  const overset = getOversetItemSpecifics({ properties, variations: product.get(FORM.VARIATIONS) });

  function reduceCategoryProperties(result, property, name) {
    if (property.required && !overset.has(name) && getSize(itemSpecifics.getIn([VALUE, name, 'value'])) === 0) {
      return result.set(name, errors.required({ name }));
    }

    return result;
  }

  function reduceProductProperties(result, specific, key) {
    const name = specific.get('name');
    const type = specific.get('type');
    const value = specific.get('value');
    const property = properties[name];

    function reduceCustomSpecifics(exists, id) {
      return exists || (id !== key && itemSpecifics.getIn([VALUE, id, 'name']) === name);
    }

    switch (type) {
      case TAXONOMY_PROPERTY.CUSTOM: {
        if (
          name in properties ||
          itemSpecifics.get('custom', EMPTY.LIST).reduce(reduceCustomSpecifics, false)
        ) {
          return result.set(key, errors.nameExist({ name }));
        }

        if (
          shopValidations.has('MaxCharactersPerName') &&
          getSize(name) > shopValidations.get('MaxCharactersPerName')
        ) {
          return result.set(key,
            errors.nameCharachtersOverLimit({ limit: shopValidations.get('MaxCharactersPerName') })
          );
        }

        if (
          shopValidations.has('MaxCharactersPerValue') &&
          getSize(getFirstValue(value)) > shopValidations.get('MaxCharactersPerValue')
        ) {
          return result.set(key,
            errors.valueCharachtersOverLimit({ limit: shopValidations.get('MaxCharactersPerValue') })
          );
        }

        return result;
      }

      default: {
        if (!property || !getSize(value) === 0) return result;

        if (!property.multiValued && getSize(value) > 1) {
          return result.set(key, errors.selectedOverLimit({ name }));
        }

        if (
          property.multiValued &&
          shopValidations.has('MaxValuesPerName') &&
          getSize(value) > shopValidations.get('MaxValuesPerName')
        ) {
          return result.set(key, errors.selectedOverLimit({ name }));
        }

        if (property.text) {
          const limit = (
            property.max_length ||
            shopValidations.get('MaxCharactersPerValue')
          );

          if (limit && getSize(getFirstValue(value)) > limit) {
            return result.set(key, errors.charachtersOverLimit({ limit, name }));
          }
        } else {
          let options = property.options;
          const affectedBy = getAffectingPropertyName({ property });

          if (affectedBy) {
            const affectingPropertyValue = getFirstValue(itemSpecifics.getIn([VALUE, affectedBy, 'value']));
            options = property.affectedBy[affectedBy][affectingPropertyValue] || [];
          }

          if (getSize(options) > 0 && getSize(value.subtract(Set(options))) > 1) {
            return result.set(key, errors.invalid({ name }));
          }
        }

        return result;
      }
    }
  }

  if (properties) {
    error = itemSpecifics
      .get(VALUE)
      .reduce(
        reduceProductProperties,
        status === STATUS[VELA].TEMPLATE
          ? error
          : reduce(properties, error, reduceCategoryProperties)
      );
  }

  return getSize(error)
    ? Map({ [ATTRIBUTE.ITEM_SPECIFICS]: error })
    : EMPTY.MAP;
}
